import React, { useCallback, useEffect, useState, useMemo } from 'react';
import {
  Card, Row, Col, Typography, Tag, Statistic, Table, message, Spin,
  Button, Space, DatePicker, Modal, Empty,
} from 'antd';
import {
  DashboardOutlined, ReloadOutlined, RiseOutlined, FallOutlined,
  UserAddOutlined, TeamOutlined, DollarOutlined, WhatsAppOutlined,
  StarOutlined, WarningOutlined, ThunderboltOutlined, LineChartOutlined,
  MessageOutlined, PictureOutlined, AppstoreOutlined,
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';
import CommunityInvitesModal from '../components/CommunityInvitesModal';

const { Title, Text } = Typography;
const fmtBRL = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtInt = (v) => Number(v || 0).toLocaleString('pt-BR');
const fmtDate = (d) => d ? dayjs(d).format('DD/MM HH:mm') : '-';

const PRESETS = [
  { key: 'hoje',   label: 'Hoje',        get: () => [dayjs(), dayjs()] },
  { key: 'ontem',  label: 'Ontem',       get: () => [dayjs().subtract(1,'day'), dayjs().subtract(1,'day')] },
  { key: '7d',     label: 'Últimos 7d',  get: () => [dayjs().subtract(6,'day'), dayjs()] },
  { key: '30d',    label: 'Últimos 30d', get: () => [dayjs().subtract(29,'day'), dayjs()] },
  { key: 'mes',    label: 'Mês atual',   get: () => [dayjs().startOf('month'), dayjs()] },
];

export default function Overview() {
  const [range, setRange] = useState(() => [dayjs(), dayjs()]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invitesModal, setInvitesModal] = useState({ open: false });
  const [emailModal, setEmailModal] = useState({ open: false, template: null, rows: [], loading: false, period: '30d' });
  const [bucketModal, setBucketModal] = useState({ open: false, bucket: null, ym: null, items: [], loading: false });

  const openBucketModal = useCallback(async (bucket, ym) => {
    setBucketModal({ open: true, bucket, ym, items: [], loading: true });
    try {
      const r = await api.get('/monitoring/receita-mensal', { params: { ym } });
      const items = r.data?.buckets?.[bucket]?.items || [];
      setBucketModal(m => ({ ...m, items, loading: false }));
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
      setBucketModal(m => ({ ...m, loading: false }));
    }
  }, []);

  const openEmailModal = useCallback(async (template, period = '30d') => {
    setEmailModal({ open: true, template, rows: [], loading: true, period });
    try {
      const r = await api.get('/monitoring/overview/email-recipients', { params: { template, period } });
      setEmailModal(m => ({ ...m, rows: r.data.rows || [], loading: false }));
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
      setEmailModal(m => ({ ...m, loading: false }));
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/monitoring/overview', {
        params: { from: range[0].format('YYYY-MM-DD'), to: range[1].format('YYYY-MM-DD') },
      });
      setData(r.data);
    } catch (err) {
      message.error('Erro: ' + (err.response?.data?.error || err.message));
    } finally { setLoading(false); }
  }, [range]);

  useEffect(() => { load(); }, [load]);

  const chipStatus = useMemo(() => {
    if (!data?.snapshot?.chips) return { open: 0, close: 0, others: 0 };
    return data.snapshot.chips.reduce((acc, c) => {
      if (c.last_state === 'open') acc.open++;
      else if (c.last_state === 'close') acc.close++;
      else acc.others++;
      return acc;
    }, { open: 0, close: 0, others: 0 });
  }, [data]);

  const receitaDeltaPct = data?.mensal?.anterior?.receita_total > 0
    ? ((data.mensal.delta_receita / data.mensal.anterior.receita_total) * 100).toFixed(1)
    : null;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            <DashboardOutlined /> Overview do Sistema
          </Title>
          <Text type="secondary">Visão geral: KPIs do período + snapshot + comparativo mensal + projeção</Text>
        </div>
        <Space wrap>
          {PRESETS.map(p => (
            <Button key={p.key} size="small" onClick={() => setRange(p.get())}>{p.label}</Button>
          ))}
          <DatePicker.RangePicker value={range} onChange={v => v && setRange(v)} format="DD/MM/YYYY" />
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Atualizar</Button>
        </Space>
      </div>

      {loading && !data && <div style={{ textAlign: 'center', padding: 40 }}><Spin size="large" /></div>}

      {data && (
        <>
          {/* Linha 1: KPIs do período */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6} lg={4}>
              <Card size="small"><Statistic title="Convocações" value={fmtInt(data.kpis_periodo.convocacoes.total)} prefix={<WhatsAppOutlined />}
                valueStyle={{ color: '#25D366' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  early:{data.kpis_periodo.convocacoes.early} · 1ª:{data.kpis_periodo.convocacoes.first} · 2ª:{data.kpis_periodo.convocacoes.second} · 3ª:{data.kpis_periodo.convocacoes.third}
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Card size="small"><Statistic title="Pgtos PIX gerados" value={fmtInt(data.kpis_periodo.pagamentos_pix_gerados.total)} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {data.kpis_periodo.pagamentos_pix_gerados.paid} paid · {data.kpis_periodo.pagamentos_pix_gerados.unpaid} unpaid
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Card size="small"><Statistic title="Pgtos recebidos" value={data.kpis_periodo.pagamentos_recebidos.count}
                prefix={<DollarOutlined />} valueStyle={{ color: '#52c41a' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>{fmtBRL(data.kpis_periodo.pagamentos_recebidos.valor)}</Text>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Card size="small"><Statistic title="Avaliações" value={fmtInt(data.kpis_periodo.avaliacoes)} prefix={<StarOutlined />} /></Card>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Card size="small"><Statistic title="Rankings enviados" value={fmtInt(data.kpis_periodo.rankings_enviados)} /></Card>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Card size="small"><Statistic title="Novos jogadores" value={fmtInt(data.kpis_periodo.novos_jogadores)} prefix={<UserAddOutlined />} />
                <Text type="secondary" style={{ fontSize: 11 }}>cadastrados no período (players)</Text>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Card size="small">
                <Statistic
                  title="Escalação (times ativos 7d)"
                  value={fmtInt(data.kpis_periodo.escalacao?.times_7d)}
                  prefix={<AppstoreOutlined />}
                  valueStyle={{ color: '#128C7E' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  30d:{fmtInt(data.kpis_periodo.escalacao?.times_30d)} · total:{fmtInt(data.kpis_periodo.escalacao?.times_total)}
                  <br />
                  views:{fmtInt(data.kpis_periodo.escalacao?.views)} · saves:{fmtInt(data.kpis_periodo.escalacao?.saves)} · broadcasts:{fmtInt(data.kpis_periodo.escalacao?.broadcasts)}
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={6} lg={4}>
              <Card size="small">
                <Statistic
                  title="Financeiro Comunidade"
                  value={fmtInt(data.kpis_periodo.financeiro_comunidade?.contas_ativadas)}
                  prefix={<DollarOutlined />}
                  valueStyle={{ color: '#128C7E' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  liberados: {fmtInt(data.kpis_periodo.financeiro_comunidade?.times_liberados)} · MP: {fmtInt(data.kpis_periodo.financeiro_comunidade?.mp_conectados)} · créditos 30d: {fmtBRL(data.kpis_periodo.financeiro_comunidade?.creditos_vendidos_30d_brl)}
                  <br />
                  saldo total: {fmtBRL(data.kpis_periodo.financeiro_comunidade?.saldo_total_brl)}
                  {data.kpis_periodo.financeiro_comunidade?.restricted_mode ? ' · restrito' : ' · geral'}
                  <br />
                  lotes 30d: {fmtInt(data.kpis_periodo.financeiro_comunidade?.lotes_30d)}
                  · recebido 30d: {fmtBRL(data.kpis_periodo.financeiro_comunidade?.pagamentos_recebidos_30d_brl)}
                  · split 30d: {fmtBRL(data.kpis_periodo.financeiro_comunidade?.split_recebido_30d_brl)}
                  {data.kpis_periodo.financeiro_comunidade?.reforcos_30d > 0 && (
                    <><br />reforços 30d: {data.kpis_periodo.financeiro_comunidade.reforcos_30d} (WhatsApp: {data.kpis_periodo.financeiro_comunidade.reforcos_whatsapp_30d})</>
                  )}
                  {data.kpis_periodo.financeiro_comunidade?.lotes_por_source_30d && Object.keys(data.kpis_periodo.financeiro_comunidade.lotes_por_source_30d).length > 0 && (
                    <><br />fontes: {Object.entries(data.kpis_periodo.financeiro_comunidade.lotes_por_source_30d).map(([s, n]) => `${s}:${n}`).join(' · ')}</>
                  )}
                </Text>
              </Card>
            </Col>
          </Row>

          {/* Linha 2: Snapshot */}
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={12} md={6}>
              <Card size="small">
                <Statistic title="Times ativos" value={fmtInt(data.snapshot.times.ativos)} prefix={<TeamOutlined />}
                  valueStyle={{ color: '#1890ff' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  status=ATIVO + subscription ATIVA · {fmtInt(data.snapshot.times.ativos_status_only)} só por status
                </Text>
                <br />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {data.snapshot.times.expirados} expirados · {data.snapshot.times.cancelados} cancelados
                </Text>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card size="small">
                <Statistic title="Jogadores em times" value={fmtInt(data.snapshot.jogadores_total)} prefix={<UserAddOutlined />} />
                <Text type="secondary" style={{ fontSize: 11 }}>cadastrados pelo organizador no bot</Text>
              </Card>
            </Col>
            <Col xs={12} md={4}>
              <Card size="small">
                <Statistic title="Jogadores usando app" value={fmtInt(data.snapshot.jogadores_comunidade?.total || 0)} prefix={<UserAddOutlined />}
                  valueStyle={{ color: '#25D366' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {fmtInt(data.snapshot.jogadores_comunidade?.com_termos || 0)} com termos · +{data.snapshot.jogadores_comunidade?.novos_30d || 0} em 30d
                </Text>
              </Card>
            </Col>
            <Col xs={24} md={10}>
              <Card size="small" title={<>💬 Saúde dos chips ({chipStatus.open}/{data.snapshot.chips.length} up)</>}>
                <Row gutter={[8, 8]}>
                  {data.snapshot.chips.map(chip => {
                    const msgChip = data.msgs_por_chip.find(m => m.instance === chip.instance);
                    return (
                      <Col xs={12} md={8} lg={6} key={chip.instance}>
                        <Space direction="vertical" size={0} style={{ width: '100%' }}>
                          <div>
                            <Tag color={chip.last_state === 'open' ? 'green' : chip.last_state === 'close' ? 'red' : 'orange'}>
                              {chip.last_state}
                            </Tag>
                            <Text style={{ fontSize: 12 }}>{chip.instance}</Text>
                          </div>
                          <Text type="secondary" style={{ fontSize: 11 }}>
                            {msgChip ? `${fmtInt(msgChip.msgs)} msgs (${msgChip.erros} err)` : '0 msgs'}
                          </Text>
                        </Space>
                      </Col>
                    );
                  })}
                </Row>
              </Card>
            </Col>
          </Row>

          {/* Engajamento da comunidade */}
          {data.snapshot?.engajamento && (
            <Card size="small" title="❤️ Engajamento da comunidade" style={{ marginBottom: 12 }}>
              <Row gutter={[12, 12]}>
                <Col xs={12} md={6}>
                  <Statistic title="Curtidas totais" value={fmtInt(data.snapshot.engajamento.curtidas_total || 0)}
                    valueStyle={{ color: '#f5222d' }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    +{fmtInt(data.snapshot.engajamento.curtidas_7d || 0)} em 7d
                  </Text>
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title="Seguindo times" value={fmtInt(data.snapshot.engajamento.seguindo_time_total || 0)}
                    valueStyle={{ color: '#25D366' }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {fmtInt(data.snapshot.engajamento.seguindo_player_total || 0)} seguindo jogadores
                  </Text>
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title="Elogios entre jogadores" value={fmtInt(data.snapshot.engajamento.elogios_total || 0)}
                    valueStyle={{ color: '#eb2f96' }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    +{fmtInt(data.snapshot.engajamento.elogios_7d || 0)} em 7d
                  </Text>
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title="Notícias publicadas" value={fmtInt(data.snapshot.engajamento.noticias_total || 0)}
                    valueStyle={{ color: '#128C7E' }} />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title="Amistosos abertos" value={fmtInt(data.snapshot.engajamento.amistosos_abertos || 0)}
                    valueStyle={{ color: '#faad14' }} />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title="Amistosos confirmados" value={fmtInt(data.snapshot.engajamento.amistosos_confirmados || 0)}
                    valueStyle={{ color: '#1890ff' }} />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic title="Denúncias abertas" value={fmtInt(data.snapshot.engajamento.denuncias_abertas || 0)}
                    valueStyle={{ color: data.snapshot.engajamento.denuncias_abertas > 0 ? '#ff4d4f' : '#8c8c8c' }} />
                </Col>
              </Row>
            </Card>
          )}

          {/* Acessos comunidade.futezap.com.br */}
          {data.snapshot?.acessos_comunidade && (
            <Card size="small" title="🌐 Acessos comunidade.futezap.com.br" style={{ marginBottom: 12 }}>
              <Row gutter={[12, 12]}>
                <Col xs={12} md={8}>
                  <Statistic title="Pageviews 24h" value={fmtInt(data.snapshot.acessos_comunidade.pageviews_24h || 0)}
                    valueStyle={{ color: '#1890ff' }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {fmtInt(data.snapshot.acessos_comunidade.visitors_24h || 0)} visitantes únicos
                  </Text>
                </Col>
                <Col xs={12} md={8}>
                  <Statistic title="Pageviews 7d" value={fmtInt(data.snapshot.acessos_comunidade.pageviews_7d || 0)}
                    valueStyle={{ color: '#1890ff' }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {fmtInt(data.snapshot.acessos_comunidade.visitors_7d || 0)} visitantes únicos
                  </Text>
                </Col>
                <Col xs={12} md={8}>
                  <Statistic title="Pageviews 30d" value={fmtInt(data.snapshot.acessos_comunidade.pageviews_30d || 0)}
                    valueStyle={{ color: '#1890ff' }} />
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    {fmtInt(data.snapshot.acessos_comunidade.visitors_30d || 0)} visitantes únicos
                  </Text>
                </Col>
                <Col xs={12} md={8}>
                  <Statistic title="Cadastros 24h" value={fmtInt(data.snapshot.acessos_comunidade.cadastros_24h || 0)}
                    valueStyle={{ color: '#25D366' }} prefix={<UserAddOutlined />} />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic title="Cadastros 7d" value={fmtInt(data.snapshot.acessos_comunidade.cadastros_7d || 0)}
                    valueStyle={{ color: '#25D366' }} prefix={<UserAddOutlined />} />
                </Col>
                <Col xs={12} md={8}>
                  <Statistic title="Cadastros 30d" value={fmtInt(data.snapshot.acessos_comunidade.cadastros_30d || 0)}
                    valueStyle={{ color: '#25D366' }} prefix={<UserAddOutlined />} />
                </Col>
              </Row>
              <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                Pageview = 1 por rota nova por sessão (não conta refresh). Visitante único = 1 sessão de navegador (dura até fechar aba).
              </Text>
            </Card>
          )}

          {/* Emails transacionais (Resend) — envios por template */}
          {Array.isArray(data.snapshot?.emails_por_template) && (
            <Card size="small" title="📧 Emails transacionais (Resend)" style={{ marginBottom: 12 }}>
              {data.snapshot.emails_por_template.length === 0 ? (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Nenhum email enviado ainda. Os envios são logados a partir do momento em que a
                  tabela email_logs foi criada — se voltar zerado depois do primeiro fluxo real
                  (aceite de convite, proposta de amistoso), tem algo errado.
                </Text>
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="template"
                  dataSource={data.snapshot.emails_por_template}
                  onRow={(record) => ({
                    onClick: () => openEmailModal(record.template, '30d'),
                    style: { cursor: 'pointer' },
                  })}
                  columns={[
                    { title: 'Template', dataIndex: 'template', key: 'template' },
                    { title: '24h', dataIndex: 'sent_24h', key: 'sent_24h', align: 'right',
                      render: (v, r) => <span>{fmtInt(v)}{r.err_24h > 0 && <Tag color="red" style={{ marginLeft: 6 }}>{r.err_24h} erro</Tag>}</span> },
                    { title: '7d', dataIndex: 'sent_7d', key: 'sent_7d', align: 'right',
                      render: (v, r) => <span>{fmtInt(v)}{r.err_7d > 0 && <Tag color="red" style={{ marginLeft: 6 }}>{r.err_7d} erro</Tag>}</span> },
                    { title: '30d', dataIndex: 'sent_30d', key: 'sent_30d', align: 'right',
                      render: (v, r) => <span>{fmtInt(v)}{r.err_30d > 0 && <Tag color="red" style={{ marginLeft: 6 }}>{r.err_30d} erro</Tag>}</span> },
                  ]}
                />
              )}
              {Array.isArray(data.snapshot?.emails_por_template) && data.snapshot.emails_por_template.length > 0 && (
                <Text type="secondary" style={{ fontSize: 11, display: 'block', marginTop: 8 }}>
                  Clique em uma linha pra ver quem recebeu.
                </Text>
              )}
            </Card>
          )}

          {/* Linha 3: Comparativo mensal */}
          <Card size="small" title={<><LineChartOutlined /> Comparativo mensal — {dayjs(data.periodo.ym_atual, 'YYYY-MM').format('MM/YYYY')} vs {dayjs(data.periodo.ym_anterior, 'YYYY-MM').format('MM/YYYY')}</>}
            style={{ marginBottom: 12 }}>
            <Row gutter={[12, 12]}>
              <Col xs={24} md={8}>
                <Statistic title="Receita atual"  value={data.mensal.atual.receita_total} prefix="R$" precision={2}
                  valueStyle={{ color: '#52c41a' }} />
              </Col>
              <Col xs={24} md={8}>
                <Statistic title="Receita anterior" value={data.mensal.anterior.receita_total} prefix="R$" precision={2} />
              </Col>
              <Col xs={24} md={8}>
                <Statistic title="Delta" value={data.mensal.delta_receita} prefix={data.mensal.delta_receita >= 0 ? <RiseOutlined /> : <FallOutlined />}
                  precision={2} valueStyle={{ color: data.mensal.delta_receita >= 0 ? '#52c41a' : '#ff4d4f' }}
                  suffix={<Text style={{ fontSize: 12 }}>R$ ({receitaDeltaPct != null ? `${receitaDeltaPct}%` : '—'})</Text>} />
              </Col>
            </Row>
            <Table
              size="small" pagination={false} style={{ marginTop: 12 }}
              dataSource={[
                { key: 'novos',      bucket: 'novos',      label: 'Novos (1ª mensalidade)', atual: data.mensal.atual.novos, ant: data.mensal.anterior.novos },
                { key: 'renov',      bucket: 'renovacoes', label: 'Renovações',              atual: data.mensal.atual.renovacoes, ant: data.mensal.anterior.renovacoes },
                { key: 'reat',       bucket: 'reativados', label: 'Reativados',              atual: data.mensal.atual.reativados, ant: data.mensal.anterior.reativados },
                { key: 'churn',      bucket: 'churn',      label: 'Churn confirmado',        atual: data.mensal.atual.churn, ant: data.mensal.anterior.churn },
              ]}
              columns={[
                { title: 'Bucket', dataIndex: 'label' },
                { title: 'Atual', render: (_, r) => (
                    <>
                      <Tag color="blue" style={{ cursor: r.atual.count > 0 ? 'pointer' : 'default' }}
                        onClick={() => r.atual.count > 0 && openBucketModal(r.bucket, data.periodo.ym_atual)}>
                        {r.atual.count}
                      </Tag>
                      {fmtBRL(r.atual.receita || r.atual.receita_perdida)}
                    </>
                  ) },
                { title: 'Anterior', render: (_, r) => (
                    <>
                      <Tag color="default" style={{ cursor: r.ant.count > 0 ? 'pointer' : 'default' }}
                        onClick={() => r.ant.count > 0 && openBucketModal(r.bucket, data.periodo.ym_anterior)}>
                        {r.ant.count}
                      </Tag>
                      {fmtBRL(r.ant.receita || r.ant.receita_perdida)}
                    </>
                  ) },
                { title: 'Δ count', render: (_, r) => {
                    const d = r.atual.count - r.ant.count;
                    return <Text type={d > 0 ? 'success' : d < 0 ? 'danger' : 'secondary'}>{d > 0 ? '+' : ''}{d}</Text>;
                  } },
              ]}
            />
          </Card>

          {/* Linha 4: Novos times por coorte */}
          <Card size="small" title="Novos times — mês atual vs anterior (com engajamento)" style={{ marginBottom: 12 }}>
            <Table
              size="small" pagination={false}
              dataSource={data.novos_times_por_coorte}
              rowKey="coorte"
              columns={[
                { title: 'Mês entrada', dataIndex: 'coorte', render: v => <Tag>{dayjs(v, 'YYYY-MM').format('MM/YYYY')}</Tag> },
                { title: 'Total', dataIndex: 'total' },
                { title: 'Pagantes', dataIndex: 'pagantes', render: v => <Tag color="green">{v}</Tag> },
                { title: 'Usaram e não pagaram', dataIndex: 'engajados_sem_pagar', render: v => <Tag color="purple">{v}</Tag> },
                { title: 'Trial médio', dataIndex: 'trial_meio_uso' },
                { title: 'Trial pouco', dataIndex: 'trial_pouco_uso' },
                { title: 'Conversão %', render: (_, r) => r.total > 0 ? `${((r.pagantes/r.total)*100).toFixed(1)}%` : '-' },
              ]}
            />
          </Card>

          {/* Linha 5: Projeção */}
          <Card size="small" title={<>🔮 Projeção — próximo mês ({dayjs(data.periodo.ym_proximo,'YYYY-MM').format('MM/YYYY')})</>}
            style={{ marginBottom: 12 }}>
            <Row gutter={[12, 12]}>
              <Col xs={12} md={6}>
                <Statistic title="Tempo médio pra pagar" value={data.projecao.conversao.avg_meses} suffix="meses" precision={2} />
                <Text type="secondary" style={{ fontSize: 11 }}>mediana {data.projecao.conversao.mediana_meses.toFixed(2)}m · {data.projecao.conversao.amostra} amostras</Text>
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Prováveis conversões" value={data.projecao.provaveis_conversoes_no_prox_mes} prefix={<ThunderboltOutlined />}
                  valueStyle={{ color: '#1890ff' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>times usando + atingem idade média</Text>
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Recorrentes ativos" value={data.projecao.recorrentes_ativos.count} prefix={<TeamOutlined />} />
                <Text type="secondary" style={{ fontSize: 11 }}>{fmtBRL(data.projecao.recorrentes_ativos.receita_potencial)} potencial</Text>
              </Col>
              <Col xs={12} md={6}>
                <Statistic title="Receita projetada" value={data.projecao.receita_projetada_prox_mes} prefix="R$" precision={2}
                  valueStyle={{ color: '#25D366' }} />
                <Text type="secondary" style={{ fontSize: 11 }}>= recorrentes + novos prováveis</Text>
              </Col>
            </Row>

            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              Times criados nos últimos 3 meses que já usam sistema (top {data.projecao.provaveis_conversoes.length}):
            </Text>
            <Table
              size="small" pagination={{ pageSize: 10, showSizeChanger: false }}
              dataSource={data.projecao.provaveis_conversoes}
              rowKey="id"
              columns={[
                { title: 'ID', dataIndex: 'id', width: 70 },
                { title: 'Time', dataIndex: 'game_name', ellipsis: true, render: (v, r) => <a href={`/customers/${r.id}/ficha`}>{v}</a> },
                { title: 'Mês entrada', dataIndex: 'coorte', width: 130, render: v => <Tag>{dayjs(v,'YYYY-MM').format('MM/YYYY')}</Tag> },
                { title: 'Jogos 30d', dataIndex: 'games_30d', width: 90 },
                { title: 'Jogadores', dataIndex: 'players', width: 90 },
              ]}
            />
          </Card>

          {/* Linha 5.5: Convites da comunidade (total geral) — cards clicaveis */}
          {data.convites && (
            <Card size="small" title="💌 Convites da comunidade (clique num card pra ver a lista + chat)" style={{ marginBottom: 12 }}>
              <Row gutter={[12, 12]}>
                <Col xs={12} md={4}>
                  <div onClick={() => setInvitesModal({ open: true, title: 'Todos os convites' })}
                       style={{ cursor: 'pointer' }}>
                    <Statistic title="Total" value={data.convites.total} />
                  </div>
                </Col>
                <Col xs={12} md={5}>
                  <div onClick={() => setInvitesModal({ open: true, direction: 'team_to_player', title: 'Time → Jogador' })}
                       style={{ cursor: 'pointer' }}>
                    <Statistic title="Time → Jogador" value={data.convites.time_para_jogador}
                      valueStyle={{ color: '#1890ff' }} />
                  </div>
                </Col>
                <Col xs={12} md={5}>
                  <div onClick={() => setInvitesModal({ open: true, direction: 'player_to_team', title: 'Jogador → Time' })}
                       style={{ cursor: 'pointer' }}>
                    <Statistic title="Jogador → Time" value={data.convites.jogador_para_time}
                      valueStyle={{ color: '#52c41a' }} />
                  </div>
                </Col>
                <Col xs={12} md={5}>
                  <div onClick={() => setInvitesModal({ open: true, status: 'PENDING', title: 'Convites pendentes' })}
                       style={{ cursor: 'pointer' }}>
                    <Statistic title="Pendentes" value={data.convites.pendentes}
                      valueStyle={{ color: '#faad14' }} />
                  </div>
                </Col>
                <Col xs={12} md={5}>
                  <div onClick={() => setInvitesModal({ open: true, status: 'ACCEPTED', title: 'Convites aceitos' })}
                       style={{ cursor: 'pointer' }}>
                    <Statistic title="Aceitos / Rejeitados" value={`${data.convites.aceitos} / ${data.convites.rejeitados}`} />
                    <Text type="secondary" style={{ fontSize: 10 }}>clique = ver aceitos</Text>
                  </div>
                </Col>
              </Row>
            </Card>
          )}

          {/* Linha 6: Banners (total geral, nao filtrado por periodo) */}
          <Card size="small" title={<><PictureOutlined /> Banners (total desde a criação)</>} style={{ marginBottom: 12 }}>
            <Table
              size="small" pagination={false}
              dataSource={data.banners}
              rowKey="id"
              columns={[
                { title: 'ID', dataIndex: 'id', width: 60 },
                { title: 'Título', dataIndex: 'title', ellipsis: true },
                { title: 'Ativo', dataIndex: 'active', width: 80, render: v => v ? <Tag color="green">ATIVO</Tag> : <Tag>OFF</Tag> },
                { title: 'Views', dataIndex: 'views', width: 100, align: 'right' },
                { title: 'Clicks', dataIndex: 'clicks', width: 100, align: 'right' },
                { title: 'CTR', dataIndex: 'ctr', width: 100, align: 'right',
                  render: v => <Tag color={v > 2 ? 'green' : v > 0.5 ? 'blue' : 'default'}>{v}%</Tag> },
              ]}
            />
          </Card>

          {/* Linha 7: Churn detalhado */}
          {data.churn_detalhado?.length > 0 && (
            <Card size="small" title={<><WarningOutlined /> Churn detalhado (mês atual)</>} style={{ marginBottom: 12 }}>
              <Table
                size="small" pagination={{ pageSize: 10, showSizeChanger: false }}
                dataSource={data.churn_detalhado}
                rowKey="id"
                columns={[
                  { title: 'ID', dataIndex: 'id', width: 70 },
                  { title: 'Time', dataIndex: 'game_name', ellipsis: true, render: (v, r) => <a href={`/customers/${r.id}/ficha`}>{v}</a> },
                  { title: 'Status', dataIndex: 'status', width: 130, render: v => <Tag color="orange">{v}</Tag> },
                  { title: 'Plano', dataIndex: 'plan_name', width: 130 },
                  { title: 'Últ. pgto', dataIndex: 'last_paid_at', width: 130, render: fmtDate },
                  { title: 'Valor', dataIndex: 'price', width: 100, align: 'right', render: v => fmtBRL(v) },
                ]}
              />
            </Card>
          )}
        </>
      )}

      <CommunityInvitesModal
        open={invitesModal.open}
        onClose={() => setInvitesModal({ open: false })}
        direction={invitesModal.direction}
        status={invitesModal.status}
        title={invitesModal.title}
      />

      <Modal
        open={emailModal.open}
        onCancel={() => setEmailModal({ open: false, template: null, rows: [], loading: false, period: '30d' })}
        footer={null}
        width={820}
        title={<span>📧 Destinatários — <code>{emailModal.template}</code></span>}
      >
        <Space style={{ marginBottom: 12 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Período:</Text>
          {['24h', '7d', '30d'].map(p => (
            <Button
              key={p}
              size="small"
              type={emailModal.period === p ? 'primary' : 'default'}
              onClick={() => openEmailModal(emailModal.template, p)}
            >
              {p}
            </Button>
          ))}
          <Text type="secondary" style={{ fontSize: 11 }}>
            {emailModal.loading ? 'carregando...' : `${emailModal.rows.length} envio(s) · limite 500`}
          </Text>
        </Space>
        {emailModal.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : emailModal.rows.length === 0 ? (
          <Empty description="Nenhum envio nesse período" />
        ) : (
          <Table
            size="small"
            rowKey="id"
            pagination={{ pageSize: 20, showSizeChanger: false }}
            dataSource={emailModal.rows}
            columns={[
              { title: 'Enviado em', dataIndex: 'created_at', width: 140, render: fmtDate },
              { title: 'Destinatário', dataIndex: 'to_email', ellipsis: true },
              { title: 'Assunto', dataIndex: 'subject', ellipsis: true, render: v => v || <Text type="secondary">—</Text> },
              { title: 'Status', dataIndex: 'ok', width: 90, align: 'center',
                render: (ok, r) => ok
                  ? <Tag color="green">enviado</Tag>
                  : <Tag color="red" title={r.error || ''}>falhou</Tag> },
            ]}
            expandable={{
              expandedRowRender: r => (
                <div style={{ fontSize: 12 }}>
                  {r.error && <div><Text strong>Erro:</Text> <Text type="danger">{r.error}</Text></div>}
                  {r.resend_id && <div><Text strong>Resend ID:</Text> <code>{r.resend_id}</code></div>}
                </div>
              ),
              rowExpandable: r => !!(r.error || r.resend_id),
            }}
          />
        )}
      </Modal>

      <Modal
        open={bucketModal.open}
        onCancel={() => setBucketModal({ open: false, bucket: null, ym: null, items: [], loading: false })}
        footer={null}
        width={880}
        title={<span>📊 {({
          novos: 'Novos (1ª mensalidade)',
          renovacoes: 'Renovações',
          reativados: 'Reativados',
          churn: 'Churn confirmado',
        }[bucketModal.bucket] || bucketModal.bucket)} — {bucketModal.ym ? dayjs(bucketModal.ym, 'YYYY-MM').format('MM/YYYY') : ''}</span>}
      >
        {bucketModal.loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
        ) : bucketModal.items.length === 0 ? (
          <Empty description="Nenhum time nesse bucket" />
        ) : (
          <>
            <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 8 }}>
              {bucketModal.items.length} time(s) · total {fmtBRL(bucketModal.items.reduce((s, i) => s + Number(i.valor || 0), 0))}
            </Text>
            <Table
              size="small"
              rowKey="customer_id"
              pagination={{ pageSize: 20, showSizeChanger: false }}
              dataSource={bucketModal.items}
              columns={[
                { title: 'ID', dataIndex: 'customer_id', width: 70 },
                { title: 'Time', dataIndex: 'game_name', ellipsis: true,
                  render: (v, r) => <a href={`/customers/${r.customer_id}/ficha`}>{v}</a> },
                { title: 'Status', dataIndex: 'status', width: 130,
                  render: v => <Tag color={v === 'ATIVO' ? 'green' : v === 'CANCELADO' ? 'red' : 'orange'}>{v}</Tag> },
                { title: 'Plano', dataIndex: 'plan', width: 130,
                  render: v => v || <Text type="secondary">—</Text> },
                { title: bucketModal.bucket === 'churn' ? 'Últ. pgto' : 'Valor', dataIndex: 'valor', width: 110, align: 'right',
                  render: v => fmtBRL(v) },
                ...(bucketModal.bucket === 'churn' ? [
                  { title: 'Expirou em', dataIndex: 'expires_at', width: 130, render: fmtDate },
                ] : []),
                ...(bucketModal.bucket === 'novos' ? [
                  { title: '1º pgto', dataIndex: 'first_ym', width: 90 },
                ] : []),
              ]}
            />
          </>
        )}
      </Modal>
    </div>
  );
}
